import { ActionType, type PlayerAction } from '../types/actions'
import type { CardData } from '../types/cards'
import type { Seat } from '../types/state'

/** Bump when a stored action would no longer reproduce the same duel. */
export const REPLAY_ENGINE_VERSION = 1

/** Replay blobs kept per account. Older rows stay as results only. */
export const REPLAY_KEEP = 10

/** Stored JSON above this skips the blob and keeps the history row. */
export const REPLAY_MAX_BYTES = 200_000

export const HISTORY_LIST_LIMIT = 50

const PASSCODE = /^\d{1,10}$/
const ACTION_TYPES = new Set<string>(Object.values(ActionType))

export interface ReplayAction {
  s: Seat
  a: PlayerAction
}

export interface ReplayDeck {
  main: string[]
  extra: string[]
  mainFoils?: string[]
  extraFoils?: string[]
}

export type ReplayMode = 'ai' | 'pvp_casual' | 'pvp_ranked' | 'pvp_event'
export type DuelHistoryResult = 'win' | 'loss' | 'draw'

export interface DuelReplay {
  version: number
  mode: ReplayMode
  seed: number
  firstPlayer: Seat
  names: [string, string]
  decks: [ReplayDeck, ReplayDeck]
  actions: ReplayAction[]
  winnerSeat: Seat | null
  reason: string | null
  turnCount: number | null
}

export interface DuelHistoryEntry {
  id: string
  replayId: string | null
  opponent: string
  mode: ReplayMode
  result: DuelHistoryResult
  reason: string | null
  turnCount: number | null
  createdAt: string
}

/** Seed, lists, and actions kept on a live PvP photo so a restart can still replay. */
export interface LiveReplayPhoto {
  seed: number
  firstPlayer: Seat
  decks: [ReplayDeck, ReplayDeck]
  actions: ReplayAction[]
  intact: boolean
}

export function replayDeckFromCards(main: CardData[], extra: CardData[]): ReplayDeck {
  const deck: ReplayDeck = {
    main: main.map((card) => String(card.id)),
    extra: extra.map((card) => String(card.id)),
  }
  if (main.some((card) => card.foil)) {
    deck.mainFoils = main.map((card) => card.foil || 'rare')
  }
  if (extra.some((card) => card.foil)) {
    deck.extraFoils = extra.map((card) => card.foil || 'rare')
  }
  return deck
}

export function replayJsonBytes(decks: [ReplayDeck, ReplayDeck], actions: ReplayAction[]): number {
  return JSON.stringify(decks).length + JSON.stringify(actions).length
}

function asSeat(value: unknown): Seat | null {
  return value === 0 || value === 1 ? value : null
}

function asName(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const name = value.trim().slice(0, 64)
  return name.length > 0 ? name : null
}

function asMode(value: unknown): ReplayMode | null {
  if (
    value === 'ai' ||
    value === 'pvp_casual' ||
    value === 'pvp_ranked' ||
    value === 'pvp_event'
  ) {
    return value
  }
  return null
}

function asResult(value: unknown): DuelHistoryResult | null {
  if (value === 'win' || value === 'loss' || value === 'draw') return value
  return null
}

function asPasscodes(value: unknown, max: number): string[] | null {
  if (!Array.isArray(value) || value.length > max) return null
  const ids: string[] = []
  for (const id of value) {
    if (typeof id !== 'string' || !PASSCODE.test(id)) return null
    ids.push(id)
  }
  return ids
}

function asFoils(value: unknown, length: number): string[] | undefined | null {
  if (value == null) return undefined
  if (!Array.isArray(value) || value.length !== length) return null
  const foils: string[] = []
  for (const foil of value) {
    if (typeof foil !== 'string' || foil.length > 32) return null
    foils.push(foil)
  }
  return foils
}

export function parseReplayDeck(raw: unknown): ReplayDeck | null {
  if (!raw || typeof raw !== 'object') return null
  const src = raw as ReplayDeck
  const main = asPasscodes(src.main, 60)
  const extra = asPasscodes(src.extra, 15)
  if (!main || !extra || main.length < 1) return null
  const mainFoils = asFoils(src.mainFoils, main.length)
  const extraFoils = asFoils(src.extraFoils, extra.length)
  if (mainFoils === null || extraFoils === null) return null
  const deck: ReplayDeck = { main, extra }
  if (mainFoils) deck.mainFoils = mainFoils
  if (extraFoils) deck.extraFoils = extraFoils
  return deck
}

export function parseReplayActions(raw: unknown): ReplayAction[] | null {
  if (!Array.isArray(raw) || raw.length > 8000) return null
  const actions: ReplayAction[] = []
  for (const row of raw) {
    if (!row || typeof row !== 'object') return null
    const src = row as ReplayAction
    const seat = asSeat(src.s)
    if (seat === null || !src.a || typeof src.a !== 'object') return null
    if (!ACTION_TYPES.has(src.a.type)) return null
    actions.push({ s: seat, a: src.a })
  }
  return actions
}

export function parseLiveReplayPhoto(raw: unknown): LiveReplayPhoto | null {
  if (!raw || typeof raw !== 'object') return null
  const src = raw as LiveReplayPhoto
  const seed = typeof src.seed === 'number' ? src.seed : Number(src.seed)
  const first = asSeat(src.firstPlayer)
  const left = parseReplayDeck(src.decks?.[0])
  const right = parseReplayDeck(src.decks?.[1])
  const actions = parseReplayActions(src.actions)
  if (!Number.isSafeInteger(seed) || seed <= 0 || first === null) return null
  if (!left || !right || !actions) return null
  return {
    seed,
    firstPlayer: first,
    decks: [left, right],
    actions,
    intact: src.intact === true,
  }
}

export function parseDuelReplay(raw: unknown): DuelReplay | null {
  if (!raw || typeof raw !== 'object') return null
  const src = raw as DuelReplay
  const version = typeof src.version === 'number' ? src.version : Number(src.version)
  const mode = asMode(src.mode)
  const seed = typeof src.seed === 'number' ? src.seed : Number(src.seed)
  const first = asSeat(src.firstPlayer)
  const leftName = asName(src.names?.[0])
  const rightName = asName(src.names?.[1])
  const left = parseReplayDeck(src.decks?.[0])
  const right = parseReplayDeck(src.decks?.[1])
  const actions = parseReplayActions(src.actions)
  const winner = src.winnerSeat == null ? null : asSeat(src.winnerSeat)
  if (src.winnerSeat != null && winner === null) return null
  if (!Number.isInteger(version) || version < 1) return null
  if (!mode || !Number.isSafeInteger(seed) || seed <= 0 || first === null) return null
  if (!leftName || !rightName || !left || !right || !actions) return null
  const turnCount =
    src.turnCount == null
      ? null
      : typeof src.turnCount === 'number' && Number.isFinite(src.turnCount)
        ? Math.max(0, Math.floor(src.turnCount))
        : null
  if (src.turnCount != null && turnCount === null) return null
  const reason =
    src.reason == null
      ? null
      : typeof src.reason === 'string'
        ? src.reason.slice(0, 32)
        : null
  if (src.reason != null && reason === null) return null
  return {
    version,
    mode,
    seed,
    firstPlayer: first,
    names: [leftName, rightName],
    decks: [left, right],
    actions,
    winnerSeat: winner,
    reason,
    turnCount,
  }
}

export function historyResult(seat: Seat, winner: Seat | null): DuelHistoryResult {
  if (winner == null) return 'draw'
  return winner === seat ? 'win' : 'loss'
}

export function isDuelHistoryResult(value: string): value is DuelHistoryResult {
  return asResult(value) !== null
}
